import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Box, TextField } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import PropTypes from 'prop-types';
import {
  Card, CardBody, Col, Container, Row,
} from 'reactstrap';
import { useHistory, useParams } from 'react-router-dom';
import DealInformation from '../shared/components/DealInformation';
import PartialPageLoader from '../shared/components/Loader/PartialPageLoader';
import StageStatusMessage from '../shared/components/StageStatusMessage';
import usePendingInvestmentDetail from '../shared/hooks/usePendingInvestmentDetail';
import getStageStatus from '../shared/utils/checkUserPermissions';
import { spacing } from '../shared/themes/appTheme';
import { updatePendingInvestment } from '../shared/services/apiGateway';
import generateRequest from '../shared/services/generateRequest';
import CloseOnConfirmModal from '../shared/components/CloseOnConfirmModal';
import getSearchParams from '../shared/utils/getSearchParams';
import { companyInformationQuestions } from '../shared/components/CompanyInfo';
import { CLOSING_DOCS_REVIEW_QUESTION_LABELS } from '../ClosingDocsReview/QuestionList';
import { getDigitalAssetGatingQuestions } from '../UploadWireInfo/QuestionList';

const styles = {
  questionSpacing: { margin: '15px 0 5px 0', fontSize: '15px' },
};

const companyInfoSummaryNames = ['countryIncorp', 'corpType'];

const getDealInfo = (deal) => ({
  dealName: deal?.companyName || deal?.hsDealObj?.dealName || deal?.hsDealObj?.company?.name || '',
  sponsorTeam: deal?.sponsorTeam || deal?.hsDealObj?.sponsorTeam?.name || '',
  iTeamMemberName: deal?.iTeamMemberName || '',
});

const getReviewQuestions = (deal) => {
  if (!deal) {
    return [];
  }

  const companyInfoValues = {
    countryIncorp: deal.companyCountryIncorp,
    corpType: deal.companyCorpType,
  };
  const closingDocsQuestions = deal.closingDocsReviewQuestions || [];
  const priorTermsQuestion = closingDocsQuestions.find((question) => (
    question.label === CLOSING_DOCS_REVIEW_QUESTION_LABELS.priorInvestmentTerms
  ));
  const questions = [
    ...companyInformationQuestions
      .filter((question) => companyInfoSummaryNames.includes(question.name))
      .map((question) => ({ ...question, value: companyInfoValues[question.name] || '' })),
    ...getDigitalAssetGatingQuestions(deal.digitalAssetsQuestions)
      .filter((question) => question.value === true),
    ...closingDocsQuestions.filter((question) => question.value === false
      && !Object.values(CLOSING_DOCS_REVIEW_QUESTION_LABELS).includes(question.label)),
    ...(deal.initialInvestment === false && priorTermsQuestion?.value === false
      ? [priorTermsQuestion] : []),
    ...(deal.companyInvestmentInformation?.yesNoQuestions || [])
      .filter((question) => question.value === true),
  ];

  return questions.map((question) => (typeof question.value === 'boolean'
    ? { ...question, value: question.value ? 'Yes' : 'No' }
    : question));
};

function QuestionAnswer({ question }) {
  if (companyInfoSummaryNames.includes(question.name)) {
    return (
      <h5 style={styles.questionSpacing}>
        <strong>{question.label}:</strong>&ensp;{question.value}
      </h5>
    );
  }

  return (
    <div>
      <h5 style={styles.questionSpacing}>{question.label}</h5>
      <h6>&emsp;<i>Answer:</i>&ensp;{question.value}</h6>
      {question.value === 'No' && question.exp && (
        <h6>&emsp;<i>Reason:</i>&ensp;{question.exp}</h6>
      )}
    </div>
  );
}

function OoiApproval() {
  const { id } = useParams();
  const closeOnConfirm = getSearchParams('closeoncompletion');
  const history = useHistory();
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [notes, setNotes] = useState('');
  const [notesError, setNotesError] = useState('');
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [stageStatus, setStageStatus] = useState({
    isEditableByRole: false,
  });

  const dealData = usePendingInvestmentDetail(id, (deal) => {
    setNotes(deal.ooiApprovalNotes || '');
    setStageStatus({
      ...getStageStatus(deal),
    });
  });

  const reviewQuestions = getReviewQuestions(dealData);
  const requiresOoiReview = !!dealData?.companyInvestmentInformation?.requiresOoiReview
    || !!dealData?.closingDocsReviewRequiresOoiReview;
  const prerequisitesCompleted = !!dealData?.stagesCompleted?.closingDocsReview;
  const dealInfo = getDealInfo(dealData);
  const canReview = requiresOoiReview
    && prerequisitesCompleted
    && stageStatus.isEditableByRole
    && !stageStatus.isCompleted;
  const isSubmitting = isApproving || isRejecting;

  const submitOoiReview = async (approved) => {
    if (!dealData || !canReview) {
      return;
    }

    const trimmedNotes = notes.trim();
    if (!approved && !trimmedNotes) {
      setNotesError('Comments are required when rejecting OOI approval.');
      return;
    }

    const reviewDate = new Date().toISOString();
    const updatedDealData = {
      ...dealData,
      ooiApprovalNotes: trimmedNotes,
      stagesCompleted: {
        ...(dealData.stagesCompleted || {}),
        ooiApproval: approved ? reviewDate : false,
      },
    };

    setNotesError('');
    setIsApproving(approved);
    setIsRejecting(!approved);
    const actions = approved ? [] : [{
      label: 'rejectDeal',
      value: { rejectionReason: 'Other', originStep: '/ooi-approval/' },
    }, {
      label: 'sendOoiApprovalRejectionEmail',
      value: true,
    }];
    try {
      await updatePendingInvestment(generateRequest(updatedDealData, actions));
      if (!closeOnConfirm) {
        history.push(`/deal-stages/${id}`);
      } else {
        setCompletionModalOpen(true);
      }
    } finally {
      setIsApproving(false);
      setIsRejecting(false);
    }
  };

  const renderQuestions = (questions) => (
    questions.length > 0 && (
      <Row className="w-100">
        <Col>
          {questions.map((question) => (
            <QuestionAnswer key={question.label} question={question} />
          ))}
        </Col>
      </Row>
    )
  );

  return (
    <>
      <Helmet>
        <title>OOI Approval</title>
      </Helmet>
      <Container className="med-width-form">
        <Card>
          {!dealData && PartialPageLoader()}
          {dealData && (
            <CardBody>
              <Row>
                <Col>
                  <h3>OOI Approval</h3>
                </Col>
              </Row>
              <Row className="w-100">
                <Col style={{ marginTop: spacing(2) }}>
                  <Box sx={{ '& > h5': { fontWeight: 'bold' } }}>
                    <DealInformation
                      dealInfo={dealInfo}
                      setDealInfo={() => false}
                    />
                  </Box>
                </Col>
              </Row>
              {renderQuestions(reviewQuestions)}
              <Row className="mt-5">
                <Col>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Additional information regarding this deal"
                    value={notes}
                    disabled={!canReview}
                    error={!!notesError}
                    helperText={notesError}
                    onChange={(event) => {
                      setNotes(event.target.value);
                      setNotesError('');
                    }}
                  />
                </Col>
              </Row>
              <Row className="mt-5">
                <Col className="d-flex justify-content-between">
                  {canReview && (
                    <LoadingButton
                      variant="outlined"
                      color="danger"
                      disabled={isSubmitting}
                      loading={isRejecting}
                      onClick={() => submitOoiReview(false)}
                    >
                      Reject
                    </LoadingButton>
                  )}
                  {canReview && (
                    <LoadingButton
                      variant="contained"
                      color="secondary"
                      disabled={isSubmitting}
                      loading={isApproving}
                      onClick={() => submitOoiReview(true)}
                    >
                      Approve
                    </LoadingButton>
                  )}
                </Col>
              </Row>
              <StageStatusMessage stageStatus={stageStatus} />
            </CardBody>
          )}
        </Card>
      </Container>
      <CloseOnConfirmModal modalOpen={completionModalOpen} />
    </>
  );
}

export default OoiApproval;

QuestionAnswer.propTypes = {
  question: PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    exp: PropTypes.string,
  }).isRequired,
};
